/**
 * MTG Singles Storefront — JavaScript
 * Gamers-Corps | gamers-corps.com
 * ─────────────────────────────────────
 * Save as: assets/mtg-singles.js
 *
 * Handles:
 *  - Tag-based filtering (color, rarity, type, format)
 *  - Vendor filtering (set)
 *  - Condition filtering
 *  - Price range filtering
 *  - In-stock toggle
 *  - Search
 *  - Sort
 *  - Condition variant selector + price update
 *  - Add to cart (Shopify AJAX API)
 *  - Mobile sidebar toggle
 *  - Active filter chips
 *  - Collapsible filter groups
 */

(function () {
  'use strict';

  // ── STATE ──────────────────────────────────────────────────────
  const state = {
    activeTags:       new Set(),
    activeVendors:    new Set(),
    activeConditions: new Set(),
    inStockOnly:      false,
    searchQuery:      '',
    priceMin:         null,
    priceMax:         null,
    sort:             'default',
  };

  // ── ELEMENTS ───────────────────────────────────────────────────
  const grid          = document.getElementById('mtg-grid');
  const resultsCount  = document.getElementById('mtg-results-count');
  const activeFilters = document.getElementById('mtg-active-filters');
  const searchInput   = document.getElementById('mtg-search-input');
  const sortSelect    = document.getElementById('mtg-sort');
  const inStockToggle = document.getElementById('mtg-in-stock-only');
  const priceMin      = document.getElementById('price-min');
  const priceMax      = document.getElementById('price-max');
  const priceApply    = document.getElementById('mtg-price-apply');
  const setSearch     = document.getElementById('mtg-set-search');
  const sidebarToggle = document.getElementById('mtg-sidebar-toggle');
  const sidebar       = document.getElementById('mtg-sidebar');

  if (!grid) return; // Not on MTG singles page

  let allCards = Array.from(grid.querySelectorAll('.mtg-card'));
  const PAGE_SIZE    = 24;        // cards revealed per Load More click
  let   visibleLimit = PAGE_SIZE; // how many matching cards are shown right now

  // ── FILTER LOGIC ───────────────────────────────────────────────
  function cardMatchesFilters(card) {
    const tags       = (card.dataset.tags || '').split(',').map(t => t.trim());
    const vendor     = (card.dataset.vendor || '').trim();
    const title      = (card.dataset.title || '').toLowerCase();
    const price      = parseInt(card.dataset.price || '0', 10) / 100;
    const inStock    = card.dataset.inStock === 'true';
    const conditions = (card.dataset.conditions || '').split(',').map(c => c.trim()).filter(Boolean);

    // Tag filters (AND logic within same group prefix, OR across colors)
    if (state.activeTags.size > 0) {
      const activeArr   = Array.from(state.activeTags);
      const colorTags   = activeArr.filter(t => t.startsWith('color:'));
      const rarityTags  = activeArr.filter(t => t.startsWith('rarity:'));
      const typeTags    = activeArr.filter(t => t.startsWith('type:'));
      const formatTags  = activeArr.filter(t => t.startsWith('format:'));

      // Colors = OR (show card if it matches ANY selected color)
      if (colorTags.length > 0 && !colorTags.some(t => tags.includes(t))) return false;
      // Rarity = OR
      if (rarityTags.length > 0 && !rarityTags.some(t => tags.includes(t))) return false;
      // Type = OR
      if (typeTags.length > 0 && !typeTags.some(t => tags.includes(t))) return false;
      // Format = OR
      if (formatTags.length > 0 && !formatTags.some(t => tags.includes(t))) return false;
    }

    // Vendor (set) filter
    if (state.activeVendors.size > 0 && !state.activeVendors.has(vendor)) return false;

    // Condition filter
    if (state.activeConditions.size > 0) {
      const hasCondition = Array.from(state.activeConditions).some(c => conditions.includes(c));
      if (!hasCondition) return false;
    }

    // In-stock filter
    if (state.inStockOnly && !inStock) return false;

    // Search
    if (state.searchQuery && !title.includes(state.searchQuery)) return false;

    // Price range
    if (state.priceMin !== null && price < state.priceMin) return false;
    if (state.priceMax !== null && price > state.priceMax) return false;

    return true;
  }

  function applyFilters() {
    let visible = 0;

    allCards.forEach(card => {
      const show = cardMatchesFilters(card);
      card.classList.toggle('mtg-card--hidden', !show);
      if (show) visible++;
    });

    updateResultsCount(visible);
    updateActiveFilterChips();
    sortCards();
    showEmptyState(visible === 0);
    applyDisplayCap();
  }

  // ── SORT ───────────────────────────────────────────────────────
  function sortCards() {
    const visibleCards = allCards.filter(c => !c.classList.contains('mtg-card--hidden'));

    visibleCards.sort((a, b) => {
      const aTitle = a.dataset.title || '';
      const bTitle = b.dataset.title || '';
      const aPrice = parseInt(a.dataset.price || '0', 10);
      const bPrice = parseInt(b.dataset.price || '0', 10);

      switch (state.sort) {
        case 'alpha-asc':   return aTitle.localeCompare(bTitle);
        case 'alpha-desc':  return bTitle.localeCompare(aTitle);
        case 'price-asc':   return aPrice - bPrice;
        case 'price-desc':  return bPrice - aPrice;
        default:            return 0;
      }
    });

    visibleCards.forEach(card => grid.appendChild(card));
  }

  // ── ACTIVE FILTER CHIPS ────────────────────────────────────────
  function updateActiveFilterChips() {
    if (!activeFilters) return;

    activeFilters.innerHTML = '';

    const allActive = [
      ...Array.from(state.activeTags).map(t => ({ label: t.replace(':', ': '), remove: () => removeTag(t) })),
      ...Array.from(state.activeVendors).map(v => ({ label: `Set: ${v}`, remove: () => removeVendor(v) })),
      ...Array.from(state.activeConditions).map(c => ({ label: `Cond: ${c}`, remove: () => removeCondition(c) })),
    ];

    if (state.priceMin !== null || state.priceMax !== null) {
      const label = `$${state.priceMin ?? 0} – $${state.priceMax ?? '∞'}`;
      allActive.push({ label, remove: clearPrice });
    }

    if (allActive.length === 0) {
      activeFilters.hidden = true;
      return;
    }

    activeFilters.hidden = false;
    allActive.forEach(({ label, remove }) => {
      const chip = document.createElement('div');
      chip.className = 'mtg-active-tag';
      chip.innerHTML = `
        <span>${label}</span>
        <button class="mtg-active-tag__remove" type="button" aria-label="Remove filter ${label}">×</button>
      `;
      chip.querySelector('button').addEventListener('click', () => { remove(); applyFilters(); });
      activeFilters.appendChild(chip);
    });
  }

  function removeTag(tag) {
    state.activeTags.delete(tag);
    const btn = grid.closest('.mtg-page')?.querySelector(`[data-filter-tag="${tag}"]`);
    if (btn) {
      if (btn.type === 'checkbox') btn.checked = false;
      else btn.classList.remove('active');
    }
  }

  function removeVendor(vendor) {
    state.activeVendors.delete(vendor);
    const cb = grid.closest('.mtg-page')?.querySelector(`[data-filter-vendor="${vendor}"]`);
    if (cb) cb.checked = false;
  }

  function removeCondition(cond) {
    state.activeConditions.delete(cond);
    const cb = grid.closest('.mtg-page')?.querySelector(`[data-filter-condition="${cond}"]`);
    if (cb) cb.checked = false;
  }

  function clearPrice() {
    state.priceMin = null;
    state.priceMax = null;
    if (priceMin) priceMin.value = '';
    if (priceMax) priceMax.value = '';
  }

  // ── UI HELPERS ─────────────────────────────────────────────────
  function updateResultsCount(count) {
    if (resultsCount) {
      resultsCount.textContent = `${count} card${count !== 1 ? 's' : ''}`;
    }
  }

  function showEmptyState(isEmpty) {
    let empty = grid.querySelector('.mtg-empty');
    if (isEmpty && !empty) {
      empty = document.createElement('div');
      empty.className = 'mtg-empty';
      empty.innerHTML = `
        <p>No cards match your filters.</p>
        <button class="mtg-clear-btn mtg-clear-btn--large" type="button">Clear filters</button>
      `;
      empty.querySelector('button').addEventListener('click', clearAllFilters);
      grid.appendChild(empty);
    } else if (!isEmpty && empty) {
      empty.remove();
    }
  }

  function clearAllFilters() {
    state.activeTags.clear();
    state.activeVendors.clear();
    state.activeConditions.clear();
    state.inStockOnly = false;
    state.searchQuery = '';
    state.priceMin = null;
    state.priceMax = null;

    document.querySelectorAll('.mtg-checkbox__input').forEach(cb => cb.checked = false);
    document.querySelectorAll('.mtg-color-btn').forEach(btn => btn.classList.remove('active'));
    if (inStockToggle) inStockToggle.checked = false;
    if (searchInput) searchInput.value = '';
    if (priceMin) priceMin.value = '';
    if (priceMax) priceMax.value = '';

    applyFilters();
  }

  // ── EVENT WIRING ───────────────────────────────────────────────

  // Color buttons
  document.querySelectorAll('.mtg-color-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const tag = btn.dataset.filterTag;
      btn.classList.toggle('active');
      if (state.activeTags.has(tag)) state.activeTags.delete(tag);
      else state.activeTags.add(tag);
      applyFilters();
    });
  });

  // Tag checkboxes (rarity, type, format)
  document.querySelectorAll('.mtg-checkbox__input[data-filter-tag]').forEach(cb => {
    cb.addEventListener('change', () => {
      if (cb.checked) state.activeTags.add(cb.dataset.filterTag);
      else state.activeTags.delete(cb.dataset.filterTag);
      applyFilters();
    });
  });

  // Vendor checkboxes (set)
  document.querySelectorAll('.mtg-checkbox__input[data-filter-vendor]').forEach(cb => {
    cb.addEventListener('change', () => {
      if (cb.checked) state.activeVendors.add(cb.dataset.filterVendor);
      else state.activeVendors.delete(cb.dataset.filterVendor);
      applyFilters();
    });
  });

  // Condition checkboxes
  document.querySelectorAll('.mtg-checkbox__input[data-filter-condition]').forEach(cb => {
    cb.addEventListener('change', () => {
      if (cb.checked) state.activeConditions.add(cb.dataset.filterCondition);
      else state.activeConditions.delete(cb.dataset.filterCondition);
      applyFilters();
    });
  });

  // In-stock toggle
  if (inStockToggle) {
    inStockToggle.addEventListener('change', () => {
      state.inStockOnly = inStockToggle.checked;
      applyFilters();
    });
  }

  // Search
  if (searchInput) {
    let searchTimer;
    searchInput.addEventListener('input', () => {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(() => {
        state.searchQuery = searchInput.value.trim().toLowerCase();
        applyFilters();
      }, 250);
    });
  }

  // Sort
  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      state.sort = sortSelect.value;
      sortCards();
    });
  }

  // Price apply
  if (priceApply) {
    priceApply.addEventListener('click', () => {
      state.priceMin = priceMin?.value !== '' ? parseFloat(priceMin.value) : null;
      state.priceMax = priceMax?.value !== '' ? parseFloat(priceMax.value) : null;
      applyFilters();
    });
  }

  // Clear all
  document.querySelectorAll('#mtg-clear-filters, #mtg-clear-filters-empty').forEach(btn => {
    btn?.addEventListener('click', clearAllFilters);
  });

  // Set search filter
  if (setSearch) {
    setSearch.addEventListener('input', () => {
      const q = setSearch.value.toLowerCase();
      document.querySelectorAll('.mtg-set-item').forEach(item => {
        const label = item.textContent.toLowerCase();
        item.style.display = label.includes(q) ? '' : 'none';
      });
    });
  }

  // Mobile sidebar toggle
  if (sidebarToggle && sidebar) {
    sidebarToggle.addEventListener('click', () => {
      sidebar.classList.toggle('mobile-open');
    });

    // Close on outside tap
    document.addEventListener('click', (e) => {
      if (sidebar.classList.contains('mobile-open') &&
          !sidebar.contains(e.target) &&
          !sidebarToggle.contains(e.target)) {
        sidebar.classList.remove('mobile-open');
      }
    });
  }

  // Collapsible filter groups
  document.querySelectorAll('.mtg-filter-group__toggle').forEach(toggle => {
    toggle.addEventListener('click', () => {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      const targetId = toggle.dataset.target;
      const body = document.getElementById(targetId);

      toggle.setAttribute('aria-expanded', !expanded);
      if (body) body.hidden = expanded;
    });
  });

  // ── VARIANT SELECTOR + PRICE UPDATE ───────────────────────────
  grid.addEventListener('click', (e) => {
    const variantBtn = e.target.closest('.mtg-variant-btn');
    if (!variantBtn || variantBtn.disabled) return;

    const card = variantBtn.closest('.mtg-card');
    if (!card) return;

    // Deactivate siblings
    card.querySelectorAll('.mtg-variant-btn').forEach(btn => btn.classList.remove('mtg-variant-btn--active'));
    variantBtn.classList.add('mtg-variant-btn--active');

    // Update price display
    const productId = card.dataset.productId;
    const priceEl = document.getElementById(`price-${productId}`);
    if (priceEl) priceEl.textContent = variantBtn.dataset.price;

    // Update ATC button variant target
    const atcBtn = card.querySelector('.mtg-atc-btn');
    if (atcBtn) {
      atcBtn.dataset.variantId = variantBtn.dataset.variantId;
      const available = variantBtn.dataset.available === 'true';
      atcBtn.disabled = !available;
      atcBtn.classList.toggle('mtg-atc-btn--disabled', !available);
      atcBtn.textContent = available ? 'Add to Cart' : 'Out of Stock';
    }
  });

  // ── ADD TO CART (Shopify AJAX) ─────────────────────────────────
  grid.addEventListener('click', async (e) => {
    const atcBtn = e.target.closest('.mtg-atc-btn');
    if (!atcBtn || atcBtn.disabled) return;

    const variantId = atcBtn.dataset.variantId;
    if (!variantId) return;

    const originalText = atcBtn.textContent;
    atcBtn.textContent = 'Adding...';
    atcBtn.disabled = true;

    try {
      const resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });

      if (!resp.ok) throw new Error(`Cart error: ${resp.status}`);

      atcBtn.textContent = '✓ Added!';
      atcBtn.style.background = 'var(--mtg-green)';

      // Update cart count bubble if your theme has one
      updateCartCount();

      setTimeout(() => {
        atcBtn.textContent = originalText;
        atcBtn.style.background = '';
        atcBtn.disabled = false;
      }, 1800);

    } catch (err) {
      console.error('Add to cart failed:', err);
      atcBtn.textContent = 'Error — Retry';
      atcBtn.style.background = 'var(--mtg-red)';
      setTimeout(() => {
        atcBtn.textContent = originalText;
        atcBtn.style.background = '';
        atcBtn.disabled = false;
      }, 2000);
    }
  });

  async function updateCartCount() {
    try {
      const resp = await fetch('/cart.js');
      const cart = await resp.json();
      const countEl = document.querySelector('.cart-count, [data-cart-count], .header__cart-count');
      if (countEl) countEl.textContent = cart.item_count;
    } catch (_) { /* non-fatal */ }
  }


  // ── INIT ───────────────────────────────────────────────────────
  applyFilters();
  // —— DISPLAY CAP ————————————————————————————————————
  // The grid holds every card in the collection. We only show `visibleLimit`
  // of the cards that currently match the filters; Load More raises the cap.
  // Inline display is used deliberately so the cap cannot be defeated by CSS
  // specificity or a stale cached stylesheet.
  function applyDisplayCap() {
    const matches = Array.from(grid.querySelectorAll('.mtg-card:not(.mtg-card--hidden)'));
    matches.forEach((card, i) => { card.style.display = i < visibleLimit ? '' : 'none'; });
    const more = document.getElementById('mtg-load-more');
    if (more) more.style.display = matches.length > visibleLimit ? '' : 'none';
  }

  // —— LOAD MORE ————————————————————————————————————
  const totalStr = document.querySelector('.mtg-header__count');

  const btn = document.createElement('button');
  btn.id          = 'mtg-load-more';
  btn.type        = 'button';
  btn.className   = 'mtg-load-more-btn';
  btn.textContent = 'Load More';
  grid.parentElement.appendChild(btn);

  btn.addEventListener('click', () => {
    visibleLimit += PAGE_SIZE;
    applyDisplayCap();
  });

  applyDisplayCap();

  // Shopify's paginate tag caps a page at 250 items. If this collection is
  // larger, pull the remaining pages in the background so that search and
  // filters always operate on the complete set, not just what is on screen.
  (async function loadRemainder() {
    const total = parseInt((totalStr ? totalStr.textContent : '').replace(/[^\d]/g, ''), 10) || 0;
    let page = 1;
    while (grid.querySelectorAll('.mtg-card').length < total && page < 25) {
      page++;
      try {
        const resp = await fetch(window.location.pathname + '?page=' + page);
        if (!resp.ok) break;
        const doc = new DOMParser().parseFromString(await resp.text(), 'text/html');
        const newCards = doc.querySelectorAll('.mtg-card');
        if (!newCards.length) break;
        newCards.forEach(card => grid.appendChild(card));
        allCards = Array.from(grid.querySelectorAll('.mtg-card'));
        applyFilters();
      } catch (e) {
        break;
      }
    }
  })();
})();